/**
 * SyncLevelSettings — the Bereich 1 e-mail sync-level controls on the Postfächer page.
 *
 *   - Per connected mailbox: a selector for its sync level (Stufe 1/2/3). Levels ABOVE
 *     the tenant cap are disabled (the api rejects them anyway — rule #2: we render the
 *     server's cap, never re-derive policy). "Standard" = the user's default for accounts
 *     without an explicit choice.
 *   - Tenant admins (tenant.manage) additionally get a cap control — the highest level
 *     any user in the Mandant may pick.
 *
 * live/mock split lives in the hooks; this component is pure presentation + mutations.
 *
 * [Source: apps/web/src/features/mailbox/components/ConnectionList.tsx] (row styling)
 * [Source: packages/shared/src/berechtigungen.ts] (labels + descriptions + value-set)
 */

import {
  EMAIL_SYNC_LEVELS,
  EMAIL_SYNC_LEVEL_LABELS,
  EMAIL_SYNC_LEVEL_DESCRIPTIONS,
  type EmailSyncLevel,
} from '@brokly/shared';
import { cn } from '../../../lib/cn';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import {
  useEmailSyncSettings,
  useSetEmailSyncLevel,
  useEmailSyncCap,
  useSetEmailSyncCap,
} from '../hooks/useEmailSyncSettings';

/** A styled native <select> for a sync level, capped at `max`. */
function LevelSelect({
  value,
  max,
  disabled,
  includeDefault,
  onChange,
  ariaLabel,
}: {
  value: EmailSyncLevel | 'default';
  max: EmailSyncLevel;
  disabled?: boolean;
  includeDefault?: boolean;
  onChange: (next: EmailSyncLevel | 'default') => void;
  ariaLabel: string;
}) {
  return (
    <select
      aria-label={ariaLabel}
      disabled={disabled}
      value={value}
      onChange={(e) =>
        onChange(e.target.value === 'default' ? 'default' : (Number(e.target.value) as EmailSyncLevel))
      }
      className={cn(
        'h-9 rounded-md border border-border bg-background px-2 text-sm outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
      )}
    >
      {includeDefault && <option value="default">Standard (Konto-Vorgabe)</option>}
      {EMAIL_SYNC_LEVELS.map((lvl) => (
        <option key={lvl} value={lvl} disabled={lvl > max}>
          {`Stufe ${lvl} · ${EMAIL_SYNC_LEVEL_LABELS[lvl]}`}
          {lvl > max ? ' (gesperrt)' : ''}
        </option>
      ))}
    </select>
  );
}

export function SyncLevelSettings() {
  const { available, settings, isLoading } = useEmailSyncSettings();
  const setLevel = useSetEmailSyncLevel();
  const { can } = useEffectivePermissions();
  const isAdmin = can('tenant.manage');
  const capQuery = useEmailSyncCap();
  const setCap = useSetEmailSyncCap();

  // Endpoint absent (module not landed) → render nothing (honest degrade).
  if (!available && !isLoading) return null;

  const cap = settings.max_email_sync_level;
  const defaultLevel = settings.default_sync_level;

  return (
    <section className="mt-8 flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold">E-Mail-Synchronisation</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Legen Sie fest, wie viel jeder Postfach-Inhalt nach Brokly synchronisiert wird.
          Höhere Stufe = weniger Inhalt. Aktuelle Beschreibung: {EMAIL_SYNC_LEVEL_DESCRIPTIONS[defaultLevel]}
        </p>
      </div>

      {/* Tenant-admin cap (highest level any user may choose). */}
      {isAdmin && (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-muted/30 px-4 py-3">
          <div className="min-w-0">
            <div className="text-sm font-medium">Maximale Stufe im Mandanten</div>
            <div className="text-xs text-muted-foreground">
              Begrenzt, wie privat Benutzer synchronisieren dürfen (Standard: Stufe 3).
            </div>
          </div>
          <LevelSelect
            ariaLabel="Maximale Sync-Stufe im Mandanten"
            value={(capQuery.data?.max_email_sync_level ?? cap) as EmailSyncLevel}
            max={3}
            disabled={setCap.isPending}
            onChange={(next) => {
              if (next !== 'default') setCap.mutate(next);
            }}
          />
        </div>
      )}

      {/* The user's default level (applies to accounts without an explicit choice). */}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">Standard für alle Postfächer</div>
          <div className="text-xs text-muted-foreground">
            {EMAIL_SYNC_LEVEL_DESCRIPTIONS[defaultLevel]}
          </div>
        </div>
        <LevelSelect
          ariaLabel="Standard-Sync-Stufe"
          value={defaultLevel}
          max={cap}
          disabled={setLevel.isPending}
          onChange={(next) => {
            if (next !== 'default') setLevel.mutate({ mailbox_connection_id: null, sync_level: next });
          }}
        />
      </div>

      {/* Per-mailbox override. */}
      {settings.mailboxes.map((m) => (
        <div
          key={m.mailbox_connection_id}
          className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-mono text-sm text-foreground">{m.email}</span>
              {/* P3: a SHARED inbox any member may configure (not just the connecting owner). */}
              {m.is_shared && (
                <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  Geteiltes Postfach
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {m.is_shared
                ? m.is_owner
                  ? 'Geteilt · von jedem Mitglied konfigurierbar'
                  : 'Geteilt · Sie sind Mitglied und dürfen die Stufe setzen'
                : m.is_explicit
                  ? 'Eigene Stufe gesetzt'
                  : 'Verwendet den Standard'}
            </div>
          </div>
          <LevelSelect
            ariaLabel={`Sync-Stufe für ${m.email}`}
            // A shared inbox shows its ONE concrete shared level (no per-user "Standard").
            value={m.is_shared ? m.sync_level : m.is_explicit ? m.sync_level : 'default'}
            max={cap}
            includeDefault={!m.is_shared}
            disabled={setLevel.isPending}
            onChange={(next) =>
              setLevel.mutate({
                mailbox_connection_id: m.mailbox_connection_id,
                // "Standard" is expressed by writing the current default level onto the
                // account (there is no per-account "unset" endpoint in this slice).
                sync_level: next === 'default' ? defaultLevel : next,
              })
            }
          />
        </div>
      ))}
    </section>
  );
}
